<template>
  <div v-if="!isExpanded" class="stop-edit-content collapsed">
    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, errors, inputClasses, inputId }"
        :input-id="`${id}-name`"
        :errors="v$.location.locationName.$errors"
        :label="t('common.paramName', { param: t('locations.locations') })"
        class="w-4"
      >
        <LocationQuickSearch
          :id="inputId"
          v-model="searchModel"
          v-bind="ariaProps"
          :errors="errors"
          :class="inputClasses"
          :disabled="disabled"
          include-trimble-results
          @clear="emit('clear')"
          @select="onItemSelected"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${id}-earliest-${internalValue.stopType}`"
        :errors="v$.requestedAppointmentEarliest.$errors"
        :label="earlyAppointmentLabel"
        :optional="
          !isPickup ||
          (!!internalValue.sequenceNumber && internalValue.sequenceNumber > 1)
        "
        class="w-4"
        help-text="mm/dd/yyyy hh:mm"
      >
        <RygenCalendar
          :id="inputId"
          v-bind="ariaProps"
          :model-value="internalValue.requestedAppointmentEarliest"
          :disabled="disabled"
          :time-zone="internalValue.location.timeZone"
          :class="inputClasses"
          :invalid="invalid"
          date-format="mm/dd/yy"
          show-icon
          show-time
          hour-format="12"
          :step-minute="15"
          default-time="8am"
          :placeholder="earlyAppointmentLabel"
          @date-select="onEarliestAppointmentSet"
          @update:model-value="onUpdateRequestedAppointmentEarliest"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${id}-latest-${internalValue.stopType}`"
        :errors="v$.requestedAppointmentLatest.$errors"
        :label="lateAppointmentLabel"
        class="w-4"
        help-text="mm/dd/yyyy hh:mm"
        optional
      >
        <RygenCalendar
          :id="inputId"
          v-bind="ariaProps"
          v-model="internalValue.requestedAppointmentLatest"
          :disabled="disabled"
          :time-zone="internalValue.location.timeZone"
          :class="inputClasses"
          :invalid="invalid"
          date-format="mm/dd/yy"
          show-icon
          show-time
          hour-format="12"
          :step-minute="15"
          default-time="5pm"
          :placeholder="lateAppointmentLabel"
          @date-select="onLatestAppointmentSet"
        />
      </InputWrapper>
    </div>
  </div>
  <div v-else class="stop-edit-content">
    <ShippingPointLocationEdit
      :id="`${id}-location`"
      v-model="internalValue.location"
      :disabled="disabled"
      allow-ficon-updates
      @address-book-select="emit('address-book-select', $event)"
      @autocomplete="onAutocomplete"
      @clear="emit('clear')"
    />
    <ShippingPointContactEdit
      :id="`${id}-contact`"
      v-model="internalValue.contact"
      :disabled="disabled || primaryContact.loading"
      :loading="primaryContact.loading"
    />

    <div class="row border-top">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${id}-earliest-${internalValue.stopType}`"
        :errors="v$.requestedAppointmentEarliest.$errors"
        :label="earlyAppointmentLabel"
        :optional="
          !isPickup ||
          (!!internalValue.sequenceNumber && internalValue.sequenceNumber > 1)
        "
        class="w-3"
        help-text="mm/dd/yyyy hh:mm"
      >
        <RygenCalendar
          :id="inputId"
          v-bind="ariaProps"
          :model-value="internalValue.requestedAppointmentEarliest"
          :disabled="disabled"
          :time-zone="internalValue.location.timeZone"
          :class="inputClasses"
          :invalid="invalid"
          date-format="mm/dd/yy"
          show-icon
          show-time
          hour-format="12"
          :step-minute="15"
          default-time="8am"
          :placeholder="earlyAppointmentLabel"
          @date-select="onEarliestAppointmentSet"
          @update:model-value="onUpdateRequestedAppointmentEarliest"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${id}-latest-${internalValue.stopType}`"
        :errors="v$.requestedAppointmentLatest.$errors"
        :label="lateAppointmentLabel"
        class="w-3"
        help-text="mm/dd/yyyy hh:mm"
        optional
      >
        <RygenCalendar
          :id="inputId"
          v-bind="ariaProps"
          v-model="internalValue.requestedAppointmentLatest"
          :disabled="disabled"
          :time-zone="internalValue.location.timeZone"
          :class="inputClasses"
          :invalid="invalid"
          date-format="mm/dd/yy"
          show-icon
          show-time
          hour-format="12"
          :step-minute="15"
          default-time="5pm"
          :placeholder="lateAppointmentLabel"
          @date-select="onLatestAppointmentSet"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-tz-${internalValue.stopType}`"
        :label="t('common.timezones')"
        class="w-2"
        optional
      >
        <TimeZoneDropdown
          :id="inputId"
          v-bind="ariaProps"
          v-model="internalValue.location.timeZone"
          :disabled="disabled || (isFiconLocation && !hasLocationUpdate)"
          :date-time-basis="internalValue.requestedAppointmentEarliest"
          :loading="isTimezoneLoading"
          :placeholder="t('common.timezones')"
        />
      </InputWrapper>
    </div>

    <div class="row">
      <WrappedSwitch
        :id="`${id}-appointment-required`"
        v-model="internalValue.shippingPointDetails.appointmentRequired"
        :label="t('shippingPoints.appointmentRequired')"
        class="w-2"
      />
      <WrappedSwitch
        v-if="showAddToAddressBookSwitch"
        :id="`${id}-save-to-address-book`"
        v-model="internalValue.saveToAddressBook"
        :disabled="disabled"
        :label="
          !isFiconLocation
            ? t('shippingPoints.saveToAddressBook')
            : t('shippingPoints.updateAddressBook')
        "
        class="w-2"
      />
      <WrappedSwitch
        :id="`${id}-requested-date-lock`"
        v-model="internalValue.requestedAppointmentDateLock"
        :label="t('shippingPoints.requestedDateLock')"
        class="w-2"
      />
      <WrappedSwitch
        :id="`${id}-requested-time-lock`"
        v-model="internalValue.requestedAppointmentTimeLock"
        :label="t('shippingPoints.requestedTimeLock')"
        class="w-2"
      />
    </div>

    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-internal-notes`"
        :label="t('orders.paramNotes', { param: t('orders.internal') })"
        class="w-6"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="internalValue.shippingPointDetails.internalNotes"
          v-bind="ariaProps"
          :disabled="disabled"
          :rows="5"
          :placeholder="t('orders.paramNotes', { param: t('orders.internal') })"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-carrier-special-instructions`"
        :label="t('shippingPoints.carrierSpecialInstructions')"
        class="w-6"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="
            internalValue.shippingPointDetails.carrierSpecialInstructions
          "
          v-bind="ariaProps"
          :disabled="disabled"
          :rows="5"
          :placeholder="t('shippingPoints.carrierSpecialInstructions')"
        />
      </InputWrapper>
    </div>

    <div class="row border-top">
      <AccessorialsEdit
        v-model="internalValue.requestedAccessorials"
        class="w-4"
      />
    </div>

    <div v-if="showOrderReferenceNumbers" class="row border-top ref-nums">
      <ReferenceNumbersEdit
        v-model="internalValue.orderReferenceNumbers"
        :levels="[ReferenceNumberTypeLevel.ORDER]"
        class="w-8"
      />
      <small class="font-light order-reference-help">
        {{ t('referenceData.orderReferenceNumbersHelp') }}
      </small>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  afterDateTime,
  beforeDateTime,
  required,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  Address,
  DailyHoursOfOperation,
  Loadable,
  Location,
  Optional,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  EmbeddableContact,
  EmbeddableLocation,
  OrderShippingPoint,
  ShipmentStopType,
} from '@/model';

import AccessorialsEdit from '@/components/accessorial/AccessorialsEdit.vue';
import { type Stop } from '@/components/entry/StopEntrySection.vue';
import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import ShippingPointContactEdit from '@/components/order/ShippingPointContactEdit.vue';
import ShippingPointLocationEdit from '@/components/order/ShippingPointLocationEdit.vue';
import ReferenceNumbersEdit from '@/components/referenceNumber/ReferenceNumbersEdit.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import Textarea from 'primevue/textarea';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: Object as PropType<Stop>,
    default: () => new OrderShippingPoint(),
  },
  id: {
    type: String,
    required: true,
  },
  stops: {
    type: Array as PropType<Stop[]>,
    default: () => [],
  },
  primaryContact: {
    type: Object as PropType<Loadable<EmbeddableContact>>,
    default: () => ({ loading: false }),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  showOrderReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
  showAddToAddressBookSwitch: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  id,
  stops,
  primaryContact,
  disabled,
  showOrderReferenceNumbers,
  showAddToAddressBookSwitch,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', Location: Location): void;
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'clear'): void;
  (event: 'remove', stop: Stop): void;
  (event: 'update:model-value', modelValue: Stop): void;
}>();

const { t } = useI18n();

const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
const internalValue: Ref<Stop> = ref(new Clazz(modelValue.value));

const isAutocompleteSelection = ref(false);

const searchModel = computed(() => {
  return internalValue.value.location.toLocation();
});

const previousStop: Ref<Optional<Stop>> = computed(() => {
  const sequenceNumber = internalValue.value.sequenceNumber ?? -1;
  const prevSequenceNumber = sequenceNumber - 1;
  if (prevSequenceNumber < 1) {
    return undefined;
  }

  return stops.value.find((s) => s.sequenceNumber === prevSequenceNumber);
});

const isPickup = computed(
  () => internalValue.value.stopType === ShipmentStopType.PICKUP,
);

const earlyAppointmentLabel = computed(() =>
  isPickup.value
    ? t('orders.requestEarlyPickup')
    : t('orders.requestEarlyDropoff'),
);

const lateAppointmentLabel = computed(() =>
  isPickup.value
    ? t('orders.requestLatePickup')
    : t('orders.requestLateDropoff'),
);

type MaybeNestedValidationRule =
  | Record<string, ValidationRule>
  | Record<string, Record<string, ValidationRule>>;
const rules = computed(() => {
  const r: Record<string, MaybeNestedValidationRule> = {
    stopType: {
      required: required(
        t('common.paramTypes', { param: t('shipments.stops') }),
      ),
    },
    requestedAppointmentEarliest: {
      before: beforeDateTime(
        () => internalValue.value.requestedAppointmentLatest,
        earlyAppointmentLabel.value,
        lateAppointmentLabel.value,
        { strict: false },
      ),
    },
    location: {},
    requestedAppointmentLatest: {},
    requestedAppointmentDateLock: {},
    requestedAppointmentTimeLock: {},
  };

  if (!isExpanded.value) {
    r.location = {
      locationName: {
        required: required(
          t('common.paramName', { param: t('locations.locations') }),
        ),
      },
    };
  }

  if (isPickup.value && internalValue.value.sequenceNumber === 1) {
    r.requestedAppointmentEarliest.required = required(
      earlyAppointmentLabel.value,
    );
  }

  if (!isPickup.value && previousStop?.value) {
    const prev = previousStop.value;
    r.requestedAppointmentEarliest.afterEarliestPickup = afterDateTime(
      () => prev.requestedAppointmentEarliest,
    );

    r.requestedAppointmentLatest.afterEarliestPickup = afterDateTime(
      () => prev.requestedAppointmentEarliest,
    );
  }

  return r;
});
const { v$ } = useValidations(internalValue, rules);

const isTimezoneLoading = ref(false);
const timezoneCancelRequest: Ref<CancelTokenSource | undefined> = ref();

const hasLocationUpdate = computed(() =>
  authorization.hasAuthority('location', 'update'),
);

const isFiconLocation = computed(
  () => !!internalValue.value.location.locationId,
);

const getDailyHoursForDay = (date: DateTime): DailyHoursOfOperation => {
  // 1-based index starting at monday
  // `dailyHoursOfOperation` starts with monday, so convert the index
  return internalValue.value.shippingPointDetails.hoursOfOperation
    .dailyHoursOfOperation[date.weekday - 1].hours;
};

const convertTimeString = (
  timeString: string,
): [hours: number, minutes: number, seconds: number] => {
  const [hoursStr, minutesStr, secondsStr] = timeString.split(':');
  const hours = Number.parseInt(hoursStr, 10);
  const minutes = Number.parseInt(minutesStr, 10);
  const seconds = Number.parseInt(secondsStr, 10);
  return [hours, minutes, seconds];
};

const onEarliestAppointmentSet = (date: DateTime | undefined) => {
  if (!date) {
    return;
  }

  if (!isFiconLocation.value) {
    return;
  }

  const dailyHoursOfOperation = getDailyHoursForDay(date);
  if (!dailyHoursOfOperation.from) {
    return;
  }

  const [hour, minute, second] = convertTimeString(dailyHoursOfOperation.from);

  internalValue.value.requestedAppointmentEarliest =
    internalValue.value.requestedAppointmentEarliest?.set({
      hour,
      minute,
      second,
    });
};

const onLatestAppointmentSet = (date: DateTime | undefined) => {
  if (!date) {
    return;
  }

  if (!isFiconLocation.value) {
    return;
  }

  const dailyHoursOfOperation = getDailyHoursForDay(date);
  if (!dailyHoursOfOperation.to) {
    return;
  }

  const [hour, minute, second] = convertTimeString(dailyHoursOfOperation.to);

  internalValue.value.requestedAppointmentLatest =
    internalValue.value.requestedAppointmentLatest?.set({
      hour,
      minute,
      second,
    });
};

const onUpdateRequestedAppointmentEarliest = (newVal?: DateTime) => {
  internalValue.value.requestedAppointmentEarliest = newVal;
  if (
    isPickup.value &&
    newVal &&
    !internalValue.value.requestedAppointmentLatest
  ) {
    let latestDate = newVal;

    if (isFiconLocation.value) {
      const dailyHoursOfOperation = getDailyHoursForDay(latestDate);
      if (!dailyHoursOfOperation.to) {
        return;
      }

      const [hour, minute, second] = convertTimeString(
        dailyHoursOfOperation.to,
      );
      latestDate = latestDate.set({ hour, minute, second });
    } else {
      const hour = latestDate.hour;
      if (hour < 17) {
        // set to 5pm
        latestDate = latestDate.set({ hour: 17, minute: 0, second: 0 });
      } else {
        // set to 3 hours later, keeping minutes/seconds intact
        latestDate = latestDate.set({ hour: hour + 3, minute: 0, second: 0 });
      }
    }
    internalValue.value.requestedAppointmentLatest = latestDate;
  }
};

const getTimezone = async () => {
  try {
    isTimezoneLoading.value = true;
    cancelExistingTimezoneRequest();

    const address = new Address({
      locality: internalValue.value.location.city,
      region: internalValue.value.location.state,
      postalCode: internalValue.value.location.postalCode,
      country: internalValue.value.location.country,
    });
    const enrichedAddress = await api.shippingPoint.getGeocodeData(
      address,
      timezoneCancelRequest.value,
    );
    if (enrichedAddress.timeZoneLocale) {
      internalValue.value.location.timeZone = enrichedAddress.timeZoneLocale;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching timezone', e);
  } finally {
    isTimezoneLoading.value = false;
  }
};
const getTimeZoneDebounced = debounce(getTimezone, 500);
const cancelExistingTimezoneRequest = () => {
  if (timezoneCancelRequest.value) {
    getTimeZoneDebounced.cancel();
    timezoneCancelRequest.value.cancel();
  }
  timezoneCancelRequest.value = axios.CancelToken.source();
};

const onAutocomplete = (isAutocompleteSelection: boolean) => {
  internalValue.value.location.hasBeenValidated = isAutocompleteSelection;
  emit('autocomplete', isAutocompleteSelection);
};

const isAddressBookLocation = (location: Location): boolean => {
  // Address book locations have IDs, Trimble results don't
  return !!location.id;
};

const onItemSelected = (item: Location) => {
  if (isAddressBookLocation(item)) {
    emit('address-book-select', item);
  } else {
    // Preserve location name and code if this is a Trimble result
    const newLocation = EmbeddableLocation.fromLocation(item);
    newLocation.locationName = internalValue.value.location.locationName;
    newLocation.code = internalValue.value.location.code;
    internalValue.value.location = newLocation;
  }

  isAutocompleteSelection.value = true;
};

watch(
  () => internalValue.value.location.postalCode,
  () => {
    if (timezoneCancelRequest.value) {
      cancelExistingTimezoneRequest();
    }
    getTimeZoneDebounced();
  },
);

watch(
  modelValue,
  (newVal) => {
    const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
    internalValue.value = new Clazz(newVal);
    v$.value.$reset();
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newVal) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
.stop-edit-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .row {
    display: flex;
    gap: var(--gap-1);
    padding: 0 var(--sp-6);

    &.ref-nums {
      flex-direction: column;
      gap: 0;

      .order-reference-help {
        margin-top: var(--sp-2);
      }
    }
  }

  .border-top {
    border-top: var(--divider);
    padding-top: var(--sp-3);
  }
}
</style>
